/**
 * 3D Flood Inundation demo scenarios (#177 Cesium-wow; #220 per-building recolor +
 * high-rise preset).
 *
 * #177 proved the "watch the water rise" mechanic over ONE representative flood-prone
 * area — the lower Albany River around **Kashechewan / Fort Albany First Nations**
 * (Canada's most-evacuated spring-flood communities; ice-jam floods on a near-flat
 * James Bay delta). The upstream signal is WSC gauge **04HA002 (Albany River above
 * Fishing Creek Island)**, the same E.F.1 hydrometric_observations data the
 * Hydrometric Gauges layer renders.
 *
 * #220 adds a SECOND demo region — downtown **Calgary** (the 2013 Alberta / Bow River
 * flood, which inundated the high-rise downtown core) — so building-flood reads
 * dramatically against tall buildings, where it was visually subtle at the low-rise
 * reserve site. Both regions are bounded so the global OSM Buildings tileset never
 * streams nationally.
 *
 * HONESTY (the legend states this): the gauge water level is a STATION-LOCAL ASSUMED
 * datum — Canada publishes no official per-station flood stage (recon §C / floodGauges
 * doctrine) — so it is NOT a physical metres-above-sea-level the water plane can use
 * directly. The water surface elevation is therefore built as
 *     surface = sampled-terrain-baseline + modelled-depth
 * where the baseline is sampled from Cesium World Terrain at runtime and the depth is
 * either the operator's manual slider or a MODELLED mapping of the gauge reading. The
 * whole layer is a what-if, not an official inundation product.
 *
 * Pure module (no Cesium import) so the level math + the building-flood classification
 * are unit-testable headless.
 */

/** A bounded lat/lon box. */
export interface FloodBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

/** A lat/lon point. */
export interface FloodPoint {
  lat: number;
  lon: number;
}

/** Oblique fly-to vantage for the wow (camera position + look angles). */
export interface FloodView {
  lat: number;
  lon: number;
  heightM: number;
  /** degrees */
  headingDeg: number;
  /** degrees */
  pitchDeg: number;
}

/** Optional upstream gauge wired to a preset's "snap to live gauge" control. The
 *  gauge level is a station-local ASSUMED datum, so the level→depth map is an explicit
 *  MODELLED transform (labelled as such), not a physical conversion. */
export interface FloodGauge {
  station: string;
  lat: number;
  lon: number;
  label: string;
  /** A small bbox around the station for the reuse of /api/hydrometric-gauges. */
  bbox: FloodBounds;
  /** Reference "normal pool" level (assumed datum, m). */
  normalLevelM: number;
  /** Base modelled scenario depth at normal pool (the flat community floods even at a
   *  modest rise). */
  scenarioBaseM: number;
  /** Amplification of every metre the gauge climbs above normal. */
  amplify: number;
}

/** A complete flood-inundation demo region. */
export interface FloodPreset {
  /** Stable id (used as the selector value + the MapView controller key). */
  id: string;
  /** Operator-facing label in the control's preset selector. */
  label: string;
  /** One-line "why this place" blurb shown under the selector. */
  blurb: string;
  /** Geographic footprint the water surface + OSM Buildings render over (bounds the
   *  global tileset/water so it never loads nationally + bounds per-frame cost). */
  bounds: FloodBounds;
  /** Region centre — anchors the local ENU frame for building clipping + the terrain
   *  baseline sample. */
  center: FloodPoint;
  /** Oblique fly-to vantage for the wow. */
  view: FloodView;
  /** Initial modelled depth on first show — a visible flood so the mechanic reads
   *  immediately without touching the slider. */
  defaultDepthM: number;
  /** Pre-sample fallback for the terrain baseline (ellipsoid height, m). Used only
   *  until Cesium World Terrain returns the real sampled height for the centre. */
  baselineFallbackM: number;
  /** Optional upstream gauge driving the "snap to live gauge" button. */
  gauge?: FloodGauge;
}

/** Modelled flood-depth control range (metres above the sampled local ground
 *  baseline). The slider + every gauge mapping clamp to this. Shared across presets so
 *  the slider behaves identically everywhere. */
export const FLOOD_MIN_DEPTH_M = 0;
export const FLOOD_MAX_DEPTH_M = 8;

/**
 * A building is flagged FLOOD-AFFECTED (solid recolor) when the modelled water touches
 * it AND it is low-rise — at or below this height (metres ≈ a 3–4-storey building).
 * Taller buildings (towers) keep the neutral tint and read as "standing in the flood"
 * via the rising translucent water + the clipped waterline, rather than being recolored
 * solid (which would over-claim that the whole structure is underwater). This absolute
 * ceiling — not a depth ratio — is what makes the low-rise Kashechewan houses glow
 * solid (fixing the #177 subtlety) while Calgary towers stay honest.
 */
export const FLOOD_LOWRISE_CEILING_M = 12;

/** The Kashechewan / Fort Albany demo (the #177 founder pick). */
export const KASHECHEWAN_PRESET: FloodPreset = {
  id: 'kashechewan',
  label: 'Kashechewan / Fort Albany',
  blurb: 'Albany River delta — Canada’s most-evacuated spring-flood community (low-rise).',
  // ~10 km across, framing both Kashechewan (north bank) and Fort Albany (delta
  // islands to the east).
  bounds: { west: -81.74, south: 52.145, east: -81.55, north: 52.235 },
  center: { lat: 52.19, lon: -81.64 },
  view: {
    lat: 52.13, // south of centre so a downward pitch looks north over the town
    lon: -81.64,
    heightM: 4200,
    headingDeg: 0,
    pitchDeg: -28,
  },
  defaultDepthM: 2,
  // James Bay lowland ground is a few m above sea level but the geoid offset there is
  // ~ −30 m, so ellipsoid height is negative; runtime sampling overrides this.
  baselineFallbackM: -27,
  gauge: {
    station: '04HA002',
    lat: 51.85381,
    lon: -82.97542,
    label: 'Albany River above Fishing Creek Island (04HA002)',
    bbox: { west: -82.97542 - 0.3, south: 51.85381 - 0.3, east: -82.97542 + 0.3, north: 51.85381 + 0.3 },
    normalLevelM: 8.5,
    scenarioBaseM: 2,
    amplify: 1,
  },
};

/** The high-rise demo (#220) — downtown Calgary, the 2013 Bow River flood that
 *  inundated the skyscraper core. No gauge wired (the control hides the gauge button);
 *  this preset exists to make building-flood read dramatically against tall buildings. */
export const CALGARY_HIGHRISE_PRESET: FloodPreset = {
  id: 'calgary-highrise',
  label: 'Calgary downtown (high-rise)',
  blurb: 'Bow River 2013 flood — downtown towers + low-rise, so building-flood reads in 3D.',
  // ~3 km over the downtown core / East Village where the Bow + Elbow flooded in 2013.
  bounds: { west: -114.085, south: 51.038, east: -114.04, north: 51.058 },
  center: { lat: 51.048, lon: -114.063 },
  view: {
    lat: 51.034, // south of downtown so a downward pitch looks north across the towers
    lon: -114.063,
    heightM: 2600,
    headingDeg: 0,
    pitchDeg: -24,
  },
  // A more dramatic default than the low-rise site: 4 m floods low-rise + podiums while
  // towers stand in the water.
  defaultDepthM: 4,
  // Downtown Calgary ground ≈ 1045 m MSL; geoid offset ≈ −16 m → ellipsoid ≈ 1029 m.
  // Runtime World-Terrain sampling overrides this.
  baselineFallbackM: 1029,
};

/**
 * The DENSE LOW-RISE demo — **Woodbridge**, the historic village core of the City of
 * Vaughan where Woodbridge Avenue crosses the Humber River. The Humber is the GTA's
 * flash-flood river (Hurricane Hazel, 1954, killed 81 people along it and remains the
 * design storm for every conservation authority in the watershed), and the village core
 * sits on the valley floor inside the regulated flood plain.
 *
 * WHY THIS SITE COMPLETES THE SET: per-building recolor classifies on
 * `feature['cesium#estimatedHeight'] <= FLOOD_LOWRISE_CEILING_M` (12 m). The two
 * existing presets both leave that classifier idle at opposite ends — Kashechewan has
 * essentially NO OSM building footprints to act on, and Calgary's downtown towers sit
 * ABOVE the ceiling and correctly stay neutral. Woodbridge is dense suburban low-rise
 * with near-complete OSM coverage under 12 m, so it is the first preset where the solid
 * `flooded` recolor fires EN MASSE rather than on a handful of structures.
 *
 * No `gauge` block: there is no WSC real-time hydrometric station wired into
 * HYDROMETRIC_STATIONS on the Humber, so the "snap to live gauge" control stays hidden
 * (the Calgary posture). Inventing a normalLevelM here would fabricate a datum.
 */
export const WOODBRIDGE_PRESET: FloodPreset = {
  id: 'woodbridge',
  label: 'Woodbridge (Humber River)',
  blurb: 'Humber River flood plain — dense low-rise village core, so building-flood reads en masse.',
  // ~5.5 km N–S × ~4 km E–W over the Humber valley through the Woodbridge core.
  bounds: { west: -79.625, south: 43.765, east: -79.575, north: 43.815 },
  center: { lat: 43.79, lon: -79.598 },
  view: {
    lat: 43.76, // south of the box so a downward pitch looks north up the Humber valley
    lon: -79.598,
    heightM: 3200,
    headingDeg: 0,
    pitchDeg: -26,
  },
  // Mid-range default: deep enough to submerge the low-rise core (so the recolor reads)
  // without pinning the slider to an implausible extreme.
  defaultDepthM: 2.5,
  // Humber valley floor at Woodbridge ≈ 170 m MSL; southern-Ontario geoid offset
  // ≈ −36 m → ellipsoid ≈ 134 m. Runtime World-Terrain sampling overrides this.
  baselineFallbackM: 134,
};

/** All flood-inundation demo presets, in selector order. */
export const FLOOD_PRESETS: readonly FloodPreset[] = [
  KASHECHEWAN_PRESET,
  CALGARY_HIGHRISE_PRESET,
  WOODBRIDGE_PRESET,
];

/** The preset shown on first enable (back-compat with #177 — Kashechewan). */
export const FLOOD_DEFAULT_PRESET_ID = KASHECHEWAN_PRESET.id;

/** Look up a preset by id, falling back to the default preset for an unknown id. */
export function presetById(id: string | null | undefined): FloodPreset {
  return FLOOD_PRESETS.find((p) => p.id === id) ?? KASHECHEWAN_PRESET;
}

// ── Back-compat exports (the #177 Kashechewan constants) ────────────────────────────
// Kept so existing imports + tests keep resolving; new code reads them off a FloodPreset.
/** @deprecated read `preset.bounds` — kept for back-compat. */
export const FLOOD_DEMO_BOUNDS = KASHECHEWAN_PRESET.bounds;
/** @deprecated read `preset.center` — kept for back-compat. */
export const FLOOD_DEMO_CENTER = KASHECHEWAN_PRESET.center;
/** @deprecated read `preset.view` — kept for back-compat. */
export const FLOOD_DEMO_VIEW = KASHECHEWAN_PRESET.view;
/** @deprecated read `preset.defaultDepthM` — kept for back-compat. */
export const FLOOD_DEFAULT_DEPTH_M = KASHECHEWAN_PRESET.defaultDepthM;
/** @deprecated read `preset.baselineFallbackM` — kept for back-compat. */
export const FLOOD_BASELINE_FALLBACK_M = KASHECHEWAN_PRESET.baselineFallbackM;
/** @deprecated read `preset.gauge` — kept for back-compat. */
export const FLOOD_DEMO_GAUGE = KASHECHEWAN_PRESET.gauge!;
/** @deprecated read `preset.gauge.bbox` — kept for back-compat. */
export const FLOOD_DEMO_GAUGE_BBOX = KASHECHEWAN_PRESET.gauge!.bbox;
/** @deprecated read `preset.gauge.normalLevelM` — kept for back-compat. */
export const GAUGE_NORMAL_LEVEL_M = KASHECHEWAN_PRESET.gauge!.normalLevelM;
/** @deprecated read `preset.gauge.scenarioBaseM` — kept for back-compat. */
export const GAUGE_SCENARIO_BASE_M = KASHECHEWAN_PRESET.gauge!.scenarioBaseM;
/** @deprecated read `preset.gauge.amplify` — kept for back-compat. */
export const GAUGE_AMPLIFY = KASHECHEWAN_PRESET.gauge!.amplify;

/** Clamp a modelled depth to the control range. */
export function clampFloodDepthM(depthM: number): number {
  if (!Number.isFinite(depthM)) return FLOOD_MIN_DEPTH_M;
  return Math.min(FLOOD_MAX_DEPTH_M, Math.max(FLOOD_MIN_DEPTH_M, depthM));
}

/**
 * The water-surface ellipsoid height = sampled ground baseline + modelled depth.
 * Both the water polygon `height` and the building clipping plane use this.
 */
export function floodSurfaceHeightM(baselineEllipsoidM: number, depthM: number): number {
  return baselineEllipsoidM + clampFloodDepthM(depthM);
}

/**
 * MODELLED mapping of a gauge water level (its assumed-datum metres) to a flood depth
 * above local ground. Clearly NOT a physical conversion — the gauge datum is station-
 * local and there is no official flood stage; this is the what-if the legend warns
 * about. A null/NaN level falls back to the default scenario depth.
 *
 * The mapping constants come from the preset's gauge; the legacy 1-arg form keeps using
 * the Kashechewan constants for back-compat.
 */
export function gaugeLevelToDepthM(
  levelM: number | null | undefined,
  gauge: Pick<FloodGauge, 'normalLevelM' | 'scenarioBaseM' | 'amplify'> = KASHECHEWAN_PRESET.gauge!,
): number {
  if (levelM === null || levelM === undefined || !Number.isFinite(levelM)) {
    return KASHECHEWAN_PRESET.defaultDepthM;
  }
  const rise = Math.max(0, levelM - gauge.normalLevelM);
  return clampFloodDepthM(gauge.scenarioBaseM + rise * gauge.amplify);
}

/** Per-building flood classification. */
export type BuildingFloodClass = 'flooded' | 'standing';

/**
 * Classify a single building for the per-building solid recolor (#220).
 *
 * A building is FLOOD-AFFECTED ('flooded' → solid recolor) when the modelled water is
 * present (depth > 0) AND the building is low-rise (≤ `lowRiseCeilingM`) — the homes /
 * low structures that genuinely flood. Taller buildings are 'standing' (kept neutral;
 * they read as towers in the rising water, not fully-submerged), so the recolor never
 * over-claims that a skyscraper is underwater.
 *
 * `estimatedHeightM` is the building's own height (e.g. Cesium OSM Buildings'
 * `cesium#estimatedHeight`); a non-finite/≤0 height is treated as not classifiable →
 * 'standing'.
 */
export function classifyBuildingFlood(
  estimatedHeightM: number | null | undefined,
  depthM: number,
  lowRiseCeilingM: number = FLOOD_LOWRISE_CEILING_M,
): BuildingFloodClass {
  if (depthM <= 0) return 'standing';
  if (estimatedHeightM === null || estimatedHeightM === undefined) return 'standing';
  if (!Number.isFinite(estimatedHeightM) || estimatedHeightM <= 0) return 'standing';
  return estimatedHeightM <= lowRiseCeilingM ? 'flooded' : 'standing';
}
